import {
	ApiError,
	type ExternalImportPage,
	type ExternalImportReport,
	type ExternalImportRow,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { EmptyState } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import {
	Button,
	ConfirmDialog,
	Drawer,
	IconButton,
	SegmentedControl,
} from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Eye, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import type { Level } from "../../app/shell/sections";
import { missingStockKey } from "./MissingStockTab";

/**
 * The External Import tab (EP10-S04, DA-10-03): the website orders waiting for
 * the store's yes, and the ones it already imported.
 *
 * **Write is the only level with controls.** At `read` the table has no
 * selection, no bulk bar and no row actions but View; the server answers
 * `403 section_read_only` to a mutation anyway, and a button that only ever
 * fails is worse than none.
 *
 * **Import Selected carries one idempotency key per press**, and the button is
 * busy until the answer lands, so a double-click creates one order per row.
 */

type Status = "pending_review" | "imported";

/** The cache key of the queue, the store first so a switch drops it. */
export function externalImportsKey(storeId: string, status: Status) {
	return [...importQueueKey(storeId), status] as const;
}

/** Both halves of the queue. */
function importQueueKey(storeId: string) {
	return ["portal", storeId, "external-imports"] as const;
}

/** A refusal's code, in words a store reads. */
export function refusalLabel(t: Translate, code: string): string {
	switch (code) {
		case "unknown_sku":
			return t("portal.orders.import.refusal.unknown_sku");
		case "import_has_errors":
			return t("portal.orders.import.refusal.import_has_errors");
		case "import_not_pending":
			return t("portal.orders.import.refusal.import_not_pending");
		default:
			return t("portal.orders.import.refusal.other");
	}
}

export function ExternalImportTab({
	storeId,
	level,
	timeZone,
}: {
	storeId: string;
	level: Level;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const [status, setStatus] = useState<Status>("pending_review");
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [refused, setRefused] = useState<Record<string, string>>({});
	const [notice, setNotice] = useState<string | null>(null);
	const [failure, setFailure] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	// The rows a delete is waiting to be confirmed for: the selection, or one
	// row's bin (ADJ-M03-4 — a single delete asks too).
	const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
	const [viewing, setViewing] = useState<ExternalImportRow | null>(null);
	const writable = level === "write";

	const page = useCursorPagination<
		ExternalImportRow,
		ExternalImportPage & { nextCursor: string | null }
	>(
		async (cursor, size, signal) => {
			const answer = await api.listPortalExternalImports({
				status,
				limit: size,
				...(cursor === null ? {} : { cursor }),
				signal,
			});
			return { ...answer, nextCursor: answer.nextCursor ?? null };
		},
		{
			queryKey: externalImportsKey(storeId, status),
			limit: 50,
			maxLimit: 200,
			enabled: storeId !== "",
		},
	);

	async function settle(report: ExternalImportReport, verb: "imported" | "deleted") {
		setSelected(new Set());
		setRefused(
			Object.fromEntries(report.rejected.map((row) => [row.importId, row.code ?? "error"])),
		);
		setNotice(
			t(`portal.orders.import.result.${verb}`, {
				applied: report.applied.length,
				rejected: report.rejected.length,
			}),
		);
		await touched();
	}

	/**
	 * What an import, a delete or a revert moves (ADJ-M03-4): the queue, the
	 * orders it creates or withdraws, and the dashboard counting them — not
	 * every cached read of the store.
	 */
	const touched = () =>
		Promise.all(
			[
				importQueueKey(storeId),
				missingStockKey(storeId),
				["portal", storeId, "orders"],
				["portal", storeId, "dashboard"],
			].map((queryKey) => queries.invalidateQueries({ queryKey })),
		);

	async function run(action: () => Promise<void>) {
		setBusy(true);
		setFailure(null);
		setNotice(null);
		try {
			await action();
		} catch (error) {
			setFailure(
				error instanceof ApiError && error.code === "revert_order_moved"
					? t("portal.orders.import.revertMoved")
					: t("portal.orders.import.failed"),
			);
		} finally {
			setBusy(false);
		}
	}

	const importRows = (ids: string[]) =>
		run(async () =>
			settle(await api.importPortalExternalOrders(ids, crypto.randomUUID()), "imported"),
		);
	const deleteRows = (ids: string[]) =>
		run(async () => settle(await api.deletePortalExternalImports(ids), "deleted"));
	const revert = (row: ExternalImportRow) =>
		run(async () => {
			await api.revertPortalExternalImport(row.importId);
			setNotice(t("portal.orders.import.reverted", { orderNo: row.orderNo ?? "" }));
			await touched();
		});

	const waiting = status === "pending_review";
	const items = page.page?.items ?? [];

	return (
		<div className="flex flex-col gap-3">
			<SegmentedControl<Status>
				aria-label={t("portal.orders.import.status.label")}
				value={status}
				onChange={(next) => {
					setStatus(next);
					setSelected(new Set());
					setRefused({});
					page.reset();
				}}
				options={[
					{ value: "pending_review", label: t("portal.orders.import.status.pending_review") },
					{ value: "imported", label: t("portal.orders.import.status.imported") },
				]}
				className="self-start"
			/>
			{notice !== null && (
				<p role="status" className="text-[12.5px] text-fg">
					{notice}
				</p>
			)}
			{failure !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failure}
				</p>
			)}

			<DataTable<ExternalImportRow>
				screenKey="portal.orders.import"
				caption={t("portal.orders.tabs.external_import")}
				t={t}
				columns={importColumns(t, locale, timeZone, refused)}
				rowKey={(row) => row.importId}
				page={{ items, nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				{...(writable && waiting
					? {
							selection: {
								selected,
								onChange: setSelected,
								bulkActions: (
									<>
										<Button
											variant="primary"
											size="sm"
											icon={Check}
											loading={busy}
											onClick={() => void importRows([...selected])}
										>
											{t("portal.orders.import.importSelected")}
										</Button>
										<Button
											variant="danger"
											size="sm"
											icon={Trash2}
											disabled={busy}
											onClick={() => setConfirmDelete([...selected])}
										>
											{t("portal.orders.import.deleteSelected")}
										</Button>
									</>
								),
							},
						}
					: {})}
				rowActions={(row) => (
					<div className="flex items-center gap-1">
						<IconButton
							icon={Eye}
							size="sm"
							variant="ghost"
							label={t("portal.orders.import.view")}
							onClick={() => setViewing(row)}
						/>
						{writable && row.status === "pending_review" && (
							<>
								<IconButton
									icon={Check}
									size="sm"
									variant="primary"
									label={t("portal.orders.import.approve")}
									disabled={busy}
									onClick={() => void importRows([row.importId])}
								/>
								<IconButton
									icon={Trash2}
									size="sm"
									variant="ghost"
									label={t("portal.orders.import.delete")}
									disabled={busy}
									onClick={() => setConfirmDelete([row.importId])}
								/>
							</>
						)}
						{writable && row.status === "imported" && row.orderStatus === "pending" && (
							<IconButton
								icon={RotateCcw}
								size="sm"
								className="rounded-full"
								label={t("portal.orders.import.revert", { orderNo: row.orderNo ?? "" })}
								disabled={busy}
								onClick={() => void revert(row)}
							/>
						)}
					</div>
				)}
				empty={
					<EmptyState
						title={t(`portal.orders.import.empty.${status}`)}
						body={t("portal.orders.import.empty.body")}
					/>
				}
			/>

			<ConfirmDialog
				open={confirmDelete !== null}
				onOpenChange={(open) => !open && setConfirmDelete(null)}
				tone="danger"
				title={t("portal.orders.import.confirmDelete.title", {
					count: confirmDelete?.length ?? 0,
				})}
				description={t("portal.orders.import.confirmDelete.body")}
				confirmLabel={t("portal.orders.import.confirmDelete.confirm")}
				cancelLabel={t("ui.actions.cancel")}
				onConfirm={async () => {
					await deleteRows(confirmDelete ?? []);
					setConfirmDelete(null);
				}}
			/>
			<PayloadDrawer row={viewing} onClose={() => setViewing(null)} />
		</div>
	);
}

/** Money a channel sent. It sends no currency, and the store bills in dollars. */
function money(amountMinor: number | undefined, currency: string | undefined, locale: string) {
	return amountMinor === undefined
		? "—"
		: formatMoney({ amountMinor, currency: currency ?? "USD" }, locale as "en" | "ar");
}

function importColumns(
	t: Translate,
	locale: string,
	timeZone: string,
	refused: Record<string, string>,
): Column<ExternalImportRow>[] {
	return [
		{
			id: "channel",
			headerKey: "portal.orders.import.columns.channel",
			cell: (row) => t(`orders.channel.${row.channel}`),
		},
		{
			id: "external",
			headerKey: "portal.orders.import.columns.external",
			mono: true,
			card: "wide",
			cell: (row) =>
				row.orderId !== undefined ? (
					<Link
						to={`/orders/${encodeURIComponent(row.orderId)}`}
						className="font-semibold text-brand-ink underline-offset-2 hover:underline"
					>
						{row.channelCustomId ?? row.reference ?? row.orderNo}
					</Link>
				) : (
					(row.channelCustomId ?? row.externalOrderId ?? row.reference ?? "—")
				),
		},
		{
			id: "customer",
			headerKey: "portal.orders.import.columns.customer",
			card: "wide",
			cell: (row) => row.customerName ?? "—",
		},
		{
			id: "address",
			headerKey: "portal.orders.import.columns.address",
			hideable: true,
			// ADJ-M03-4: the city's name, never its id.
			cell: (row) => row.payload.addressLine ?? row.payload.cityName ?? "—",
		},
		{
			id: "items",
			headerKey: "portal.orders.import.columns.items",
			align: "end",
			cell: (row) => <span className="tabular-nums">{row.lineCount}</span>,
		},
		{
			id: "total",
			headerKey: "portal.orders.import.columns.total",
			align: "end",
			cell: (row) => money(row.totalMinor, row.currency, locale),
		},
		{
			id: "received",
			headerKey: "portal.orders.import.columns.received",
			hideable: true,
			cell: (row) =>
				formatDateTime(row.channelCreatedAt ?? row.createdAt, {
					locale: locale as "en" | "ar",
					timeZone,
				}),
		},
		{
			id: "match",
			headerKey: "portal.orders.import.columns.match",
			card: "wide",
			cell: (row) => <MatchCell row={row} refusal={refused[row.importId]} />,
		},
	];
}

/** Match state, the row's own flags, and — after a press — why it stayed. */
function MatchCell({ row, refusal }: { row: ExternalImportRow; refusal: string | undefined }) {
	const { t } = useLocale();
	const unmatched = row.unmatchedSkus ?? [];
	return (
		<span className="flex flex-wrap items-center gap-1">
			{row.status === "imported" ? (
				<StatusChip tone="success" size="sm">
					{t("portal.orders.import.importedAs", { orderNo: row.orderNo ?? "" })}
				</StatusChip>
			) : unmatched.length === 0 ? (
				<StatusChip tone="success" size="sm">
					{t("portal.orders.import.matched")}
				</StatusChip>
			) : (
				<StatusChip tone="danger" size="sm">
					{t("portal.orders.import.unmatched", { count: unmatched.length })}
				</StatusChip>
			)}
			{row.validationErrors.length > 0 && (
				<StatusChip tone="danger" size="sm">
					{t("portal.orders.import.refusal.import_has_errors")}
				</StatusChip>
			)}
			{row.possibleDuplicate && (
				<StatusChip tone="warning" size="sm">
					{t("portal.orders.import.duplicate")}
				</StatusChip>
			)}
			{refusal !== undefined && (
				<StatusChip tone="danger" size="sm">
					{refusalLabel(t, refusal)}
				</StatusChip>
			)}
		</span>
	);
}

/** View: the channel's order mapped onto the fields an order has. */
function PayloadDrawer({ row, onClose }: { row: ExternalImportRow | null; onClose: () => void }) {
	const { t, locale } = useLocale();
	const payload = row?.payload;
	const fields: [string, string | undefined][] = [
		["customer", payload?.customerName],
		["phone", payload?.customerPhone],
		["email", payload?.customerEmail],
		["address", payload?.addressLine],
		["city", payload?.cityName],
		["deliveryDate", payload?.deliveryDate],
		["notes", payload?.notes],
		["cod", money(payload?.codAmountMinor, row?.currency, locale)],
	];
	return (
		<Drawer
			open={row !== null}
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.orders.import.viewTitle", {
				ref: row?.channelCustomId ?? row?.reference ?? "",
			})}
			size="md"
			closeLabel={t("ui.actions.close")}
		>
			<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-[13px]">
				{fields.map(([key, value]) => (
					<div key={key} className="contents">
						<dt className="text-fg-muted">{t(`portal.orders.import.field.${key}`)}</dt>
						<dd>{value === undefined || value === "" ? "—" : value}</dd>
					</div>
				))}
			</dl>
			<h3 className="mt-4 mb-2 text-[13px] font-semibold">
				{t("portal.orders.import.field.lines")}
			</h3>
			<ul className="flex flex-col gap-1 text-[13px]">
				{(payload?.lines ?? []).map((line) => (
					<li key={line.position} className="flex justify-between gap-2">
						<span className="font-mono">
							{line.sku}
							{row?.unmatchedSkus?.includes(line.sku) && (
								<StatusChip tone="danger" size="sm" className="ms-2">
									{t("portal.orders.import.refusal.unknown_sku")}
								</StatusChip>
							)}
						</span>
						<span className="tabular-nums">× {line.qty}</span>
					</li>
				))}
			</ul>
		</Drawer>
	);
}
